/**
 * Handschutzplan-Modul · Baustein 5 (Aug 2026) — Owner-Detailseite.
 *
 * Zeigt den generierten Plan (Content-Schema v1 aus Baustein 3) in
 * lesbarer Tabellenform (was/wann/womit/wie) plus den Pflichtabschnitt
 * „Erste Hilfe bei Hautkontakt". WORM-Beleg-Download, Neu-Generieren.
 * `feuchtarbeit_eingeschaetzt` wird bewusst NICHT angezeigt
 * (Betreiberfreigabe 4a.i — interner Generierungskontext).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  FileWarning,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  KATEGORIE_LABELS,
  fehltAktuellerBeleg,
  formatDateDe,
  generateFehlerMeldung,
  istPruefungFaellig,
} from "@/lib/handschutzplan";

function PlanTabelle({ zeilen }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
      <table className="w-full text-sm" data-testid="hsp-plan-tabelle">
        <thead>
          <tr className="bg-slate-900 text-white text-left">
            <th className="px-4 py-3 font-semibold w-[22%]">Was</th>
            <th className="px-4 py-3 font-semibold w-[26%]">Wann</th>
            <th className="px-4 py-3 font-semibold w-[26%]">Womit</th>
            <th className="px-4 py-3 font-semibold w-[26%]">Wie</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, idx) => (
            <tr
              key={idx}
              className="border-t border-slate-100 align-top"
              data-testid={`hsp-zeile-${idx}`}
            >
              <td className="px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                  {KATEGORIE_LABELS[z.kategorie] || z.kategorie}
                </div>
                <div className="font-medium text-slate-900 mt-0.5">{z.was}</div>
              </td>
              <td className="px-4 py-3 text-slate-700">{z.wann}</td>
              <td className="px-4 py-3 text-slate-700">{z.womit}</td>
              <td className="px-4 py-3 text-slate-700">{z.wie}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErsteHilfe({ eintraege }) {
  if (!eintraege?.length) return null;
  return (
    <div
      className="border border-red-200 rounded-lg bg-red-50 p-4"
      data-testid="hsp-erste-hilfe"
    >
      <h2 className="font-heading text-base md:text-lg font-bold text-red-900">
        Erste Hilfe bei Hautkontakt
      </h2>
      <ul className="mt-2 space-y-2">
        {eintraege.map((e, idx) => (
          <li key={idx} className="text-sm text-red-900 flex gap-2">
            <span className="font-semibold shrink-0">{e.produkt}:</span>
            <span>{e.massnahme}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HandschutzplanDetail() {
  const { einsatzbereichId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [ebName, setEbName] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [plakatBusy, setPlakatBusy] = useState(false);
  const [plakatFormat, setPlakatFormat] = useState("a4");
  const [plakatOrientation, setPlakatOrientation] = useState("portrait");

  const load = useCallback(() => {
    api
      .listEinsatzbereiche(true)
      .then((ebs) => {
        const eb = (ebs || []).find((e) => e.id === einsatzbereichId);
        if (eb) setEbName(eb.name);
      })
      .catch(() => {});
    api
      .getHandschutzplan(einsatzbereichId)
      .then((p) => {
        setPlan(p);
        setLoaded(true);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) setLoaded(true);
        else if (status === 403) setForbidden(true);
        else setErrorMsg("Der Handschutzplan konnte nicht geladen werden.");
      });
  }, [einsatzbereichId]);

  useEffect(load, [load]);

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const doc = await api.generateHandschutzplan(einsatzbereichId);
      setPlan(doc);
      setLoaded(true);
      if (doc?.worm_status === "error") {
        toast.warning(
          "Plan erstellt — der Nachweis-Beleg konnte jedoch nicht abgelegt werden."
        );
      } else {
        toast.success("Handschutzplan wurde erstellt.");
      }
    } catch (err) {
      toast.error(generateFehlerMeldung(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    const docNumber = plan?.last_worm_document_number;
    if (!docNumber) return;
    try {
      // UX-Korrektur (29.08.2026): lesbares PDF statt rohem JSON —
      // das JSON bleibt der bindende WORM-Zeuge im Hintergrund.
      const { blob } = await api.fetchHandschutzplanBelegPdfBlob(docNumber);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Der Nachweis-Beleg konnte nicht geladen werden.");
    }
  };

  const handlePlakatDownload = async () => {
    // Baustein 9 · Async-Umstellung (28.02.2026): Zwei-Schritt-Flow.
    // 1. ``prepare`` startet Bild-Kette im Hintergrund oder meldet Reuse.
    // 2. Bei ``generating`` wird alle 2,5 s ``plakat-status`` gepollt bis
    //    ``ready``/``error``. Der Download-Endpoint selbst rendert dann
    //    nur noch (kein Ensure inline mehr).
    setPlakatBusy(true);
    const POLL_MS = 2500;
    const HARD_TIMEOUT_MS = 180000;
    let cancelled = false;
    const download = async () => {
      const { blob } = await api.downloadHandschutzplanPlakat(
        einsatzbereichId,
        plakatFormat,
        plakatOrientation,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CLARTEXT-Handschutzplan-${ebName || einsatzbereichId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Plakat-PDF wurde erstellt.");
    };
    try {
      const prep = await api.preparePlakat(einsatzbereichId);
      if (prep.status === "ready") {
        await download();
      } else if (prep.status === "error") {
        toast.error(
          prep.error ||
            "Das Plakat konnte nicht erstellt werden — bitte erneut versuchen.",
        );
      } else {
        toast.info(
          "Plakat wird erstellt — beim ersten Aufruf kann das bis zu zwei Minuten dauern.",
        );
        const start = Date.now();
        while (!cancelled) {
          if (Date.now() - start > HARD_TIMEOUT_MS) {
            toast.error(
              "Die Erstellung dauert länger als erwartet — bitte später erneut versuchen.",
            );
            return;
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, POLL_MS));
          // eslint-disable-next-line no-await-in-loop
          const st = await api.getPlakatStatus(einsatzbereichId);
          if (st.status === "ready") {
            // eslint-disable-next-line no-await-in-loop
            await download();
            return;
          }
          if (st.status === "error") {
            toast.error(
              st.error ||
                "Das Plakat konnte nicht erstellt werden — bitte erneut versuchen.",
            );
            return;
          }
          // sonst weiter pollen (idle/generating)
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 429 || status === 503) {
        toast.error(
          "Die Bild-Erstellung ist derzeit blockiert — bitte später erneut versuchen.",
        );
      } else {
        toast.error(
          "Das Plakat konnte nicht erstellt werden — bitte erneut versuchen.",
        );
      }
    } finally {
      setPlakatBusy(false);
    }
  };

  const content = plan?.content;
  const titel =
    content?.titel ||
    `Hautschutz- und Handschutzplan${ebName ? ` — ${ebName}` : ""}`;
  const faellig = istPruefungFaellig(plan);
  const belegFehlt = fehltAktuellerBeleg(plan);

  return (
    <div
      className="max-w-4xl mx-auto space-y-6"
      data-testid="handschutzplan-detail-page"
    >
      <Link
        to="/unternehmer/handschutzplan"
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
        data-testid="hsp-detail-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Alle Einsatzbereiche
      </Link>

      {forbidden ? (
        <div
          className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-4 text-sm"
          data-testid="handschutzplan-detail-forbidden"
        >
          Das Gefahrstoff-Modul ist für Ihren Betrieb nicht freigeschaltet.
        </div>
      ) : errorMsg ? (
        <div
          className="border border-red-200 bg-red-50 text-red-900 rounded-md p-4 text-sm"
          data-testid="handschutzplan-detail-error"
        >
          {errorMsg}
        </div>
      ) : !loaded ? (
        <div className="text-slate-500 text-sm" data-testid="hsp-detail-loading">
          Lade…
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
                Handschutzplan{ebName ? ` · ${ebName}` : ""}
              </p>
              <h1
                className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2"
                data-testid="hsp-detail-titel"
              >
                {titel}
              </h1>
              {plan?.generated_at && (
                <p className="text-sm text-slate-600 mt-2" data-testid="hsp-detail-meta">
                  Generiert am {formatDateDe(plan.generated_at)} · Nächste
                  Prüfung fällig am{" "}
                  {formatDateDe(plan.naechste_pruefung_faellig_am)}
                  {faellig && (
                    <span
                      className="ml-2 inline-flex items-center rounded-full bg-red-100 text-red-800 text-[11px] font-semibold px-2 py-0.5"
                      data-testid="hsp-detail-due-badge"
                    >
                      Zur Prüfung fällig
                    </span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={handleGenerate}
              disabled={busy}
              data-testid="hsp-detail-generate-btn"
              className="shrink-0 inline-flex items-center gap-2 rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {plan?.generated_at ? "Neu generieren" : "Jetzt generieren"}
            </button>
          </div>

          {busy && (
            <div
              className="text-xs text-slate-500"
              data-testid="hsp-detail-generate-hint"
            >
              Erstellung läuft — kann bis zu zwei Minuten dauern…
            </div>
          )}

          {plan && belegFehlt && (
            <div
              className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-3 text-sm flex items-start gap-2"
              data-testid="hsp-worm-warning"
            >
              <FileWarning className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Für die letzte Generierung liegt noch kein Nachweis-Beleg vor.
                Beim nächsten erfolgreichen Lauf wird er automatisch abgelegt.
              </span>
            </div>
          )}

          {!plan ? (
            <div
              className="border border-slate-200 bg-white rounded-lg p-8 text-center"
              data-testid="hsp-detail-empty"
            >
              <p className="text-slate-600 text-sm">
                Für diesen Einsatzbereich wurde noch kein Handschutzplan
                generiert.
              </p>
            </div>
          ) : (
            <>
              {content?.zeilen?.length > 0 && (
                <PlanTabelle zeilen={content.zeilen} />
              )}
              <ErsteHilfe eintraege={content?.erste_hilfe_hautkontakt} />

              {/* Baustein 6: druckfertiges Plakat (Format-Wahl 2×2). */}
              <div
                className="border border-slate-200 bg-white rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid="hsp-plakat-box"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-slate-900">
                    Plakat zum Aushängen
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Druckfertiges PDF mit Bildern je Kategorie.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <select
                    value={plakatFormat}
                    onChange={(e) => setPlakatFormat(e.target.value)}
                    disabled={plakatBusy}
                    data-testid="hsp-plakat-format-select"
                    className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                  >
                    <option value="a4">A4</option>
                    <option value="a3">A3</option>
                  </select>
                  <select
                    value={plakatOrientation}
                    onChange={(e) => setPlakatOrientation(e.target.value)}
                    disabled={plakatBusy}
                    data-testid="hsp-plakat-orientation-select"
                    className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                  >
                    <option value="portrait">Hochformat</option>
                    <option value="landscape">Querformat</option>
                  </select>
                  <button
                    onClick={handlePlakatDownload}
                    disabled={plakatBusy}
                    data-testid="hsp-plakat-download-btn"
                    className="inline-flex items-center gap-2 rounded-md bg-teal-800 text-white text-sm font-medium px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {plakatBusy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Plakat herunterladen (PDF)
                  </button>
                </div>
              </div>
              {plakatBusy && (
                <div
                  className="text-xs text-slate-500"
                  data-testid="hsp-plakat-busy-hint"
                >
                  Beim ersten Download werden die Bilder erstellt — das kann
                  bis zu zwei Minuten dauern…
                </div>
              )}

              {plan.last_worm_document_number && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <span data-testid="hsp-detail-beleg-nr">
                    Nachweis-Beleg: {plan.last_worm_document_number}
                  </span>
                  <button
                    onClick={handleDownload}
                    data-testid="hsp-worm-download-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Beleg herunterladen (PDF)
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
